import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import { useState } from 'react';

import { trpc } from '@/services/trpc';

function RecordingPlayer({ recordingId }: { recordingId: string }) {
  const { t } = useLingui();
  const [requested, setRequested] = useState(false);
  const playback = trpc.threads.getCallRecordingPlayback.useQuery(
    { id: recordingId },
    {
      enabled: requested,
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
  if (!requested || playback.isError) {
    return (
      <div className='space-y-2'>
        {playback.isError && (
          <p className='text-sm text-destructive'>
            <Trans>Could not load recording.</Trans>
          </p>
        )}
        <Button
          onClick={() => {
            setRequested(true);
            if (requested) void playback.refetch();
          }}
        >
          <Trans>Load recording</Trans>
        </Button>
      </div>
    );
  }
  if (!playback.data)
    return (
      <p className='text-sm text-muted-foreground'>
        <Trans>Loading recording…</Trans>
      </p>
    );

  return (
    <div className='space-y-2'>
      <audio
        controls
        preload='metadata'
        src={playback.data.url}
        className='w-full'
        aria-label={t`Phone call recording`}
      >
        <track kind='captions' />
      </audio>
      <Button size='sm' variant='ghost' onClick={() => void playback.refetch()}>
        <Trans>Refresh playback link</Trans>
      </Button>
    </div>
  );
}

export function CallRecordings({ threadId }: { threadId: string }) {
  const { t } = useLingui();
  const recordings = trpc.threads.getCallRecordings.useQuery(
    { id: threadId },
    {
      refetchInterval: 15_000,
    },
  );
  const labels = {
    recording: t`Recording in progress`,
    processing: t`Processing recording`,
    ready: t`Recording ready`,
    failed: t`Recording unavailable — retry scheduled`,
    absent: t`No recording was produced`,
    expired: t`Recording expired`,
  };

  return (
    <section className='space-y-3 rounded-md border p-3'>
      <h4 className='text-sm font-semibold'>
        <Trans>Call recording</Trans>
      </h4>
      {recordings.isError ? (
        <p className='text-sm text-destructive'>
          <Trans>Could not load recording status.</Trans>
        </p>
      ) : recordings.isLoading ? (
        <p className='text-sm text-muted-foreground'>
          <Trans>Loading recording…</Trans>
        </p>
      ) : recordings.data?.length ? (
        recordings.data.map(recording => (
          <div key={recording.id} className='space-y-2'>
            <p className='text-sm'>{labels[recording.status]}</p>
            {recording.durationSeconds !== undefined && (
              <p className='text-xs text-muted-foreground'>{t`Full call recording: ${Math.floor(recording.durationSeconds / 60)} min ${recording.durationSeconds % 60} sec. Includes operator transfers; the transcript covers the AI conversation.`}</p>
            )}
            {recording.status === 'ready' && (
              <RecordingPlayer recordingId={recording.id} />
            )}
          </div>
        ))
      ) : (
        <p className='text-sm text-muted-foreground'>
          <Trans>No recording for this call.</Trans>
        </p>
      )}
    </section>
  );
}
